// src/pages/Terms.jsx
import React from "react";
import { Link } from "react-router-dom";
import {
  FiFileText,
  FiUser,
  FiDollarSign,
  FiAlertTriangle,
  FiActivity,
  FiGlobe,
} from "react-icons/fi";

const Terms = () => {
  const lastUpdated = "December 15, 2024";
  const effectiveDate = "January 1, 2025";

  const sections = [
    {
      id: "acceptance-of-terms",
      title: "Acceptance of Terms",
      icon: FiFileText,
      content: [
        {
          subtitle: "Agreement to Terms",
          details: [
            "By accessing or using EduConsultancy services, you agree to be bound by these Terms and Conditions",
            "These terms constitute a legally binding agreement between you and EduConsultancy",
            "If you do not agree to these terms, you must not use our services",
            "Continued use of our services constitutes acceptance of any updated terms",
          ],
        },
        {
          subtitle: "Eligibility Requirements",
          details: [
            "You must be at least 16 years old to use our services independently",
            "Users under 18 require parent or guardian consent and supervision",
            "You must provide accurate and complete information during registration",
            "You represent that you have the legal capacity to enter into this agreement",
            "Corporate users must have proper authorization to bind their organization",
          ],
        },
      ],
    },
    {
      id: "services-description",
      title: "Description of Services",
      icon: FiGlobe,
      content: [
        {
          subtitle: "Educational Consultancy Services",
          details: [
            "University and college application assistance and guidance",
            "Academic program selection and career counseling",
            "Standardized test preparation (SAT, TOEFL, IELTS, etc.)",
            "Scholarship research and application support",
            "Visa application assistance and documentation support",
            "Interview preparation and coaching services",
          ],
        },
        {
          subtitle: "Digital Platform Services",
          details: [
            "Online application tracking and management system",
            "Document upload and storage capabilities",
            "Communication tools with counselors and advisors",
            "Educational resources and preparation materials",
            "Progress monitoring and milestone tracking",
            "Mobile application access and functionality",
          ],
        },
        {
          subtitle: "Additional Services",
          details: [
            "Travel planning and accommodation assistance",
            "Pre-departure orientation and support",
            "Post-arrival assistance and ongoing support",
            "Academic support services while studying abroad",
            "Career guidance and internship placement assistance",
          ],
        },
      ],
    },
    {
      id: "user-obligations",
      title: "User Responsibilities and Obligations",
      icon: FiUser,
      content: [
        {
          subtitle: "Account Management",
          details: [
            "Maintain the confidentiality and security of your account credentials",
            "Notify us immediately of any unauthorized use of your account",
            "Keep your profile information accurate and up-to-date",
            "Use only one account per individual unless specifically authorized",
            "Comply with all platform security requirements and recommendations",
          ],
        },
        {
          subtitle: "Information Accuracy",
          details: [
            "Provide truthful, accurate, and complete information in all applications",
            "Submit authentic academic transcripts and test scores",
            "Disclose any relevant academic or disciplinary history",
            "Ensure all documentation is current and valid",
            "Report any changes in circumstances that may affect applications",
          ],
        },
        {
          subtitle: "Prohibited Activities",
          details: [
            "Submitting false or misleading information in applications",
            "Sharing account access with unauthorized individuals",
            "Attempting to circumvent platform security measures",
            "Using our services for fraudulent or illegal purposes",
            "Interfering with other users' access to or use of services",
            "Reverse engineering or attempting to extract source code",
            "Uploading malicious software or harmful content",
          ],
        },
      ],
    },
    {
      id: "payment-terms",
      title: "Payment Terms and Billing",
      icon: FiDollarSign,
      content: [
        {
          subtitle: "Service Fees",
          details: [
            "All fees are quoted in USD unless otherwise specified",
            "Fees vary based on services selected and complexity of applications",
            "Custom service packages may have individually negotiated rates",
            "Third-party fees (application fees, test fees) are separate and additional",
            "Government fees and visa costs are not included in our service fees",
          ],
        },
        {
          subtitle: "Payment Processing",
          details: [
            "Payment is required before service delivery begins",
            "We accept major credit cards, bank transfers, and approved payment methods",
            "Installment plans may be available for comprehensive service packages",
            "All payments are processed through secure, PCI-compliant systems",
            "Payment confirmations will be provided for all transactions",
          ],
        },
        {
          subtitle: "Refund Policy",
          details: [
            "Refunds are available within 14 days of service purchase if no work has begun",
            "Partial refunds may be available for unused services in multi-service packages",
            "No refunds for completed services or after university applications are submitted",
            "Third-party fees and government charges are non-refundable",
            "Refund requests must be submitted in writing with supporting documentation",
            "Processing time for approved refunds is 5-10 business days",
          ],
        },
      ],
    },
    {
      id: "service-limitations",
      title: "Service Limitations and Disclaimers",
      icon: FiAlertTriangle,
      content: [
        {
          subtitle: "No Guarantee of Admission",
          details: [
            "We cannot guarantee admission to any educational institution",
            "University admission decisions are made solely by the institutions",
            "Success depends on many factors beyond our control",
            "We provide guidance and support but cannot ensure specific outcomes",
            "Past success rates do not predict future results",
          ],
        },
        {
          subtitle: "Third-Party Dependencies",
          details: [
            "University policies and requirements may change without notice",
            "Visa regulations and immigration laws are subject to government changes",
            "Test dates and requirements are controlled by testing organizations",
            "Application deadlines and procedures are set by institutions",
            "We are not responsible for third-party service disruptions",
          ],
        },
        {
          subtitle: "Platform Availability",
          details: [
            "Services are provided 'as is' and 'as available'",
            "We may temporarily suspend services for maintenance or updates",
            "Internet connectivity and technical issues may affect service delivery",
            "We strive for 99.9% uptime but cannot guarantee continuous availability",
            "Users are responsible for maintaining compatible devices and software",
          ],
        },
      ],
    },
    {
      id: "intellectual-property",
      title: "Intellectual Property Rights",
      icon: FiActivity,
      content: [
        {
          subtitle: "Our Intellectual Property",
          details: [
            "All platform content, including text, graphics, logos, and software, is our property",
            "Educational materials and resources are protected by copyright",
            "Trademarks and service marks are owned by EduConsultancy",
            "Users are granted limited, non-exclusive license to use our services",
            "Unauthorized copying or distribution of our content is prohibited",
          ],
        },
        {
          subtitle: "User-Generated Content",
          details: [
            "You retain ownership of content you submit (essays, personal statements, etc.)",
            "You grant us license to use your content for providing services",
            "We may use anonymized content for service improvement and training",
            "You represent that you have rights to all content you submit",
            "You will not submit content that infringes others' intellectual property rights",
          ],
        },
        {
          subtitle: "DMCA Compliance",
          details: [
            "We respect intellectual property rights and comply with DMCA",
            "Copyright infringement reports should be sent to our designated agent",
            "We will promptly investigate and respond to valid infringement claims",
            "Repeat infringers may have their access terminated",
            "Counter-notification procedures are available for disputed claims",
          ],
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-xl">
              <FiFileText size={32} className="text-white" />
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-purple-800 bg-clip-text text-transparent mb-4">
            Terms & Conditions
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Please read these terms carefully before using EduConsultancy
            services. These terms govern your use of our platform and services.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto text-sm">
            <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
              <span>Last updated:</span>
              <time className="font-semibold">{lastUpdated}</time>
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
              <span>Effective:</span>
              <time className="font-semibold">{effectiveDate}</time>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 dark:from-amber-400/10 dark:to-orange-400/10 rounded-2xl p-6 mb-8 border border-amber-200/50 dark:border-amber-700/50">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
              <FiAlertTriangle size={16} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-amber-800 dark:text-amber-200 mb-2">
                Important Notice
              </h3>
              <p className="text-amber-700 dark:text-amber-300 text-sm leading-relaxed">
                These Terms and Conditions are legally binding. By using our
                services, you acknowledge that you have read, understood, and
                agree to be bound by these terms. If you do not agree with any
                part of these terms, please do not use our services.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Sections */}
        {sections.map((section, index) => (
          <div
            key={section.id}
            className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                <section.icon size={24} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                {index + 1}. {section.title}
              </h2>
            </div>

            <div className="space-y-6">
              {section.content.map((item, itemIndex) => (
                <div key={itemIndex}>
                  <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                    {item.subtitle}
                  </h3>
                  <ul className="space-y-2">
                    {item.details.map((detail, detailIndex) => (
                      <li
                        key={detailIndex}
                        className="flex items-start gap-3 text-slate-600 dark:text-slate-400"
                      >
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                        <span className="leading-relaxed">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Additional Important Sections */}
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            7. Privacy and Data Protection
          </h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            Your privacy is important to us. Our collection, use, and protection
            of your personal information is governed by our Privacy Policy,
            which is incorporated into these Terms by reference.
          </p>
          <div className="grid gap-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Data Collection
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                We collect information necessary to provide educational
                consultancy services and improve your experience.
              </p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Data Security
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                We implement industry-standard security measures to protect your
                personal and academic information.
              </p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Third-Party Sharing
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                We share information with universities and partners only as
                necessary to provide our services.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            8. Limitation of Liability
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <p className="text-red-800 dark:text-red-300 font-semibold mb-2">
                IMPORTANT LIMITATION
              </p>
              <p className="text-red-700 dark:text-red-400 text-sm leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, EDUCONSULTANCY SHALL NOT
                BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF
                PROFITS, DATA, USE, OR OTHER INTANGIBLE LOSSES.
              </p>
            </div>
            <ul className="space-y-2">
              {[
                "Our total liability shall not exceed the amount paid for services in the 12 months preceding the claim",
                "We are not liable for delays or failures in university admissions processes",
                "We are not responsible for changes in immigration laws or visa rejections",
                "Force majeure events (natural disasters, pandemics, etc.) excuse performance delays",
                "Some jurisdictions do not allow exclusion of certain warranties or limitation of liability",
              ].map((item, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-slate-600 dark:text-slate-400"
                >
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            9. Dispute Resolution
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Informal Resolution
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
                Before filing any formal dispute, you agree to first contact us
                to seek informal resolution. We commit to working in good faith
                to resolve any issues.
              </p>
              <ul className="space-y-2">
                {[
                  "Contact our support team within 30 days of the issue arising",
                  "Provide detailed description of the problem and desired resolution",
                  "Allow 30 days for us to investigate and respond",
                  "Participate in good faith discussions to resolve the matter",
                ].map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-slate-600 dark:text-slate-400"
                  >
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Binding Arbitration
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
                If informal resolution fails, disputes will be resolved through
                binding arbitration rather than court litigation.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
                  <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Arbitration Rules
                  </h4>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">
                    Governed by American Arbitration Association Commercial
                    Arbitration Rules
                  </p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
                  <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Location
                  </h4>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">
                    Arbitration conducted in your home state or via video
                    conference
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Class Action Waiver
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                You agree that disputes will be resolved individually and not as
                part of a class action, collective action, or representative
                proceeding.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            10. Governing Law and Jurisdiction
          </h2>
          <div className="grid gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Applicable Law
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                These Terms shall be governed by and construed in accordance
                with the laws of the State of Delaware, United States, without
                regard to its conflict of law provisions.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                International Users
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
                For users outside the United States, these terms comply with
                applicable local consumer protection laws:
              </p>
              <ul className="space-y-2">
                {[
                  "EU users retain rights under European consumer protection directives",
                  "UK users are protected under UK consumer rights legislation",
                  "Canadian users have protections under provincial consumer protection acts",
                  "Australian users retain rights under Australian Consumer Law",
                ].map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-slate-600 dark:text-slate-400"
                  >
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            11. Termination and Suspension
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Termination by You
              </h3>
              <ul className="space-y-2">
                {[
                  "You may terminate your account at any time by contacting customer support",
                  "Termination does not relieve you of payment obligations for services already provided",
                  "You may request deletion of personal data subject to legal retention requirements",
                  "Completed application materials cannot be withdrawn from submitted applications",
                ].map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-slate-600 dark:text-slate-400"
                  >
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Termination by Us
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
                We may suspend or terminate your access for violations of these
                terms, including:
              </p>
              <ul className="space-y-2">
                {[
                  "Providing false or misleading information",
                  "Engaging in fraudulent activities",
                  "Violating intellectual property rights",
                  "Disrupting services for other users",
                  "Non-payment of fees after notice and opportunity to cure",
                  "Violation of applicable laws or regulations",
                ].map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-slate-600 dark:text-slate-400"
                  >
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            12. Changes to Terms
          </h2>
          <div className="space-y-4">
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              We reserve the right to modify these Terms at any time. When we
              make material changes, we will:
            </p>
            <ul className="space-y-2">
              {[
                "Provide at least 30 days advance notice of material changes",
                "Send notification to your registered email address",
                "Post prominent notice on our website and platform",
                "Highlight specific changes in the updated terms",
                "Allow you to terminate your account if you disagree with changes",
              ].map((item, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-slate-600 dark:text-slate-400"
                >
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl mt-4">
              <p className="text-blue-800 dark:text-blue-300 text-sm">
                <strong>Important:</strong> Continued use of our services after
                the effective date of updated terms constitutes acceptance of
                the changes. If you do not agree to the updated terms, you must
                stop using our services.
              </p>
            </div>
          </div>
        </div>

        {/* Contact and Legal Information */}
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 dark:from-purple-400/10 dark:to-pink-400/10 rounded-2xl p-8 border border-purple-200/50 dark:border-purple-700/50">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
              <FiActivity size={24} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
              Legal Contact Information
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-3">
                General Legal Inquiries
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-1">
                Email: legal@educonsultancy.com
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-3">
                Phone: +977 9876543210
              </p>

              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Business Address
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                EduConsultancy Legal Department
                <br />
                Bhaisepati
                <br />
                Lalitpur
                <br />
                Nepal
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Dispute Resolution
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-1">
                Email: disputes@educonsultancy.com
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-3">
                Response time: 5-7 business days
              </p>

              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                DMCA Agent
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                Email: dmca@educonsultancy.com
                <br />
                For copyright infringement reports only
              </p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-white/50 dark:bg-slate-800/50 rounded-xl">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              <strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00
              PM GMT. Emergency legal matters requiring immediate attention
              should be clearly marked as "URGENT" in the subject line.
            </p>
          </div>
        </div>

        {/* Final Disclaimer */}
        <div className="mt-12 p-6 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400 text-center leading-relaxed">
            These Terms and Conditions constitute the entire agreement between
            you and EduConsultancy regarding the use of our services. If any
            provision of these terms is found to be invalid or unenforceable,
            the remaining provisions will continue to be valid and enforceable.
            This document was last reviewed by legal counsel on {lastUpdated}{" "}
            and complies with applicable international consumer protection laws.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
